import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { useCart } from '@shared/cart-react';
import { Dots, Modal } from '@shared/cove-ui';
import { toast } from '@shared/react-ui-core';
import AffirmVerificationModal from '@website/components/affirm/AffirmVerificationModal';
import QualifiedModal from '@website/components/affirm/qualified-modal';
import { localStore } from '@website/libs/storage';
import { verifyAffirm, voidAffirm } from '@website/services/orderSvc';
import { toDollars } from '@website/utils';
import { navigate, Script } from 'gatsby';
import { isEmpty } from 'lodash';

export interface CheckoutObject {
  merchant: {
    user_confirmation_url: string;
    user_cancel_url: string;
    user_confirmation_url_action: string;
    name: string;
  };
  shipping: {
    name: {
      first: string;
      last: string;
    };
    address: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
    };
    phone_number: string;
    email: string;
  };
  items: {
    display_name: string;
    sku: string;
    unit_price: number;
    qty: number;
    item_image_url: string;
    item_url: string;
    categories: string[][];
  }[];
  discounts: Record<
    string,
    {
      discount_amount: number;
      discount_display_name: string;
    }
  >;
  metadata: { cartId: string } & Record<string, string>;
  shipping_amount: number;
  tax_amount: number;
  total: number;
}
export interface Affirm {
  jsReady: () => void;
  checkout: (checkoutObject: CheckoutObject) => { open: () => void };
  ui: {
    refresh: () => void;
  };
}

export type AffirmErrorType = '' | 'unexpected' | 'amount' | 'expired';

export type AffirmContext = Partial<
  Affirm & {
    voidAffirmTransaction: () => Promise<any>;
    affirmErrorType: AffirmErrorType;
    setAffirmErrorType: React.Dispatch<React.SetStateAction<AffirmErrorType>>;
  }
>;

const affirmContext = createContext<AffirmContext>({});

const useAffirm = () => {
  const context = React.useContext(affirmContext);
  if (context === undefined) {
    throw new Error(`useAffirm must be used within a AffirmProviders`);
  }
  return context;
};

export const withAffirm = <P extends object>(
  Component: React.FC<P & { affirm: AffirmContext }>,
) => {
  const WrappedComponent: React.FC<P> = (props) => {
    return <Component {...props} affirm={useAffirm()} />;
  };
  return WrappedComponent;
};

const AffirmCheckoutTokenHandler: React.FC = () => {
  const cart = useCart();
  const search =
    (typeof window !== 'undefined' && window.location.search) || '';
  const queryParams = search.split('=');
  var affirmCheckoutToken = '';

  if (queryParams[0] === '?checkout_token') {
    affirmCheckoutToken = queryParams[1];
  }

  const [showQualifiedModal, setShowQualifiedModal] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const isEmptyCart = isEmpty(cart);
    if (isEmptyCart) return;
    if (!cart?.addAffirm) return;
    if (!affirmCheckoutToken) return;
    if (cart?.affirmId) return;
    if (verified) return;

    setVerified(true);
    const verifyAffirmToken = async (token: string) => {
      setIsLoading(true);
      try {
        const result = await verifyAffirm(token);
        const data = result?.data;
        const success =
          (result.status === 200 &&
            data?.amount &&
            data?.amount > 0 &&
            data?.id) ||
          false;

        if (success) {
          await cart.addAffirm({
            id: data.id,
            amount: toDollars(data.amount),
            expires: Date.now() + 1000 * 60 * 60 * 24 * 14,
          });
          setShowQualifiedModal(true);
        }

        return result;
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        toast.error('Had an error while qualifying with Affirm');
      } finally {
        setIsLoading(false);
      }
    };

    verifyAffirmToken(affirmCheckoutToken);
  }, [verified, affirmCheckoutToken, cart]);

  const verificationMessage = 'Confirming your order through Affirm';

  return (
    <>
      <AffirmVerificationModal open={isLoading} message={verificationMessage} />
      <QualifiedModal
        open={showQualifiedModal}
        onConfirm={() => {
          setShowQualifiedModal(false);
          // this is to clear the checkout_token from the url
          const path =
            (typeof window !== 'undefined' && window?.location?.pathname) ||
            '/checkout/payment';
          navigate(path);
        }}
      />
    </>
  );
};

const AffirmValidate: React.FC = () => {
  const cart = useCart();
  const { voidAffirmTransaction, affirmErrorType, setAffirmErrorType } =
    useAffirm();
  const [loading, setLoading] = useState(false);
  const [checkAffirm, setCheckAffirm] = useState(false);

  useEffect(() => {
    if (
      cart?.affirmAmount !== undefined &&
      cart?.totals?.unformattedTotals?.orderTotalWithTax !== undefined
    ) {
      const timer = setTimeout(() => {
        setCheckAffirm(true);
      }, 5000);
      return () => {
        clearTimeout(timer);
      };
    }
    return () => undefined;
  }, [cart]);

  useEffect(() => {
    if (
      checkAffirm &&
      cart?.affirmAmount !== undefined &&
      cart?.totals?.unformattedTotals?.orderTotalWithTax !== undefined
    ) {
      if (
        cart?.affirmId &&
        cart?.affirmAmount !==
          cart?.totals?.unformattedTotals?.orderTotalWithTax
      ) {
        setAffirmErrorType?.('amount');
      }
    }
  }, [checkAffirm, setAffirmErrorType, cart]);

  useEffect(() => {
    if (!cart?.affirmExpires) return;
    if (cart?.affirmId && cart?.affirmExpires < Date.now()) {
      setAffirmErrorType?.('expired');
    }
  }, [setAffirmErrorType, cart]);

  const handleClose = async () => {
    try {
      setLoading(true);
      const res = await voidAffirmTransaction?.();
      if (JSON.parse(res?.body).status === 'captured') {
        localStore.removeItem('customer-info');
        localStore.removeItem('payment');
        await cart.clear();
      }
    } finally {
      setLoading(false);
      setAffirmErrorType?.('');
    }
  };

  return (
    <Modal
      open={!!affirmErrorType}
      onOpenChange={(open) => {
        if (!!affirmErrorType && !open) {
          handleClose();
        }
      }}
    >
      <Modal.Content className="w-full max-w-[720px] border border-cove-dove bg-white px-cove-25 py-cove-50 md:py-cove-75">
        <h4 className="mb-cove-15 text-center text-h4">
          {affirmErrorType === 'expired'
            ? 'Your approval with Affirm has expired.'
            : affirmErrorType === 'amount'
              ? 'You have updated your system.'
              : 'Something unexpected happened with Affirm.'}
        </h4>
        <p className="text-center text-base">
          To checkout with Affirm, please repeat the steps to prequalify.
        </p>
        {loading && (
          <div className="mt-cove-15 flex w-full justify-center">
            <Dots className="bg-cove-blue" />
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};

const AffirmProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const cart = useCart();
  const [affirm, setAffirm] = useState<Affirm | null>(null);
  const [loading, setLoading] = useState(true);
  const [affirmErrorType, setAffirmErrorType] = useState<AffirmErrorType>('');

  useEffect(() => {
    if (typeof window !== 'undefined' && loading) {
      const check = () => {
        if (window?.affirm?.jsReady?.()) {
          setAffirm(window.affirm);
          setLoading(false);
        }
      };
      const timer = setInterval(check, 100);
      check();
      return () => clearInterval(timer);
    }
    return () => {};
  }, [loading]);

  const voidAffirmTransaction = useCallback(async () => {
    if (!cart?.affirmId) return;
    try {
      const response = await voidAffirm(cart.affirmId);
      if (response.status === 200) {
        await cart.removeAffirm();
        toast('Your Affirm order has been cancelled');
      } else {
        const error = new Error(response) as Error & { response: any };
        error.response = response;
        throw error;
      }
      return response;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toast.error('There was an error cancelling your Affirm order');
      throw error;
    }
  }, [cart]);

  return (
    <>
      {cart?.cartId && <AffirmScript />}
      <affirmContext.Provider
        value={{
          ...(affirm || {}),
          voidAffirmTransaction,
          affirmErrorType,
          setAffirmErrorType,
        }}
      >
        <>{children}</>
        <AffirmCheckoutTokenHandler />
        <AffirmValidate />
      </affirmContext.Provider>
    </>
  );
};

const apiEnv =
  process.env.GATSBY_API_ENV || process.env.NODE_ENV || 'development';

const AFFIRM_PUBLIC_API_KEY =
  apiEnv === 'production' ? 'DC35237K5IR13YA2' : 'FJVY5F14J9E31AL3';
const AFFIRM_SCRIPT_URL =
  apiEnv === 'production'
    ? 'https://cdn1.affirm.com/js/v2/affirm.js'
    : 'https://cdn1-sandbox.affirm.com/js/v2/affirm.js';
const script = `_affirm_config = {
  "public_api_key":  "${AFFIRM_PUBLIC_API_KEY}",
  script:          "${AFFIRM_SCRIPT_URL}"
}; try{
(function(m,g,n,d,a,e,h,c){var b=m[n]||{},k=document.createElement(e),p=document.getElementsByTagName(e)[0],l=function(a,b,c){return function(){a[b]._.push([c,arguments])}};b[d]=l(b,d,"set");var f=b[d];b[a]={};b[a]._=[];f._=[];b._=[];b[a][h]=l(b,a,h);b[c]=function(){b._.push([h,arguments])};a=0;for(c="set add save post open empty reset on off trigger ready setProduct".split(" ");a<c.length;a++)f[c[a]]=l(b,d,c[a]);a=0;for(c=["get","token","url","items"];a<c.length;a++)f[c[a]]=function(){};k.async=
!0;k.src=g[e];p.parentNode.insertBefore(k,p);delete g[e];f(g);m[n]=b})(window,_affirm_config,"affirm","checkout","ui","script","ready","jsReady");}
catch(e){console.error(e)}`;

const AffirmScript = () => {
  return (
    <Script
      type={'text/javascript'}
      strategy="post-hydrate"
      onError={() => console.log(`failure loading Affirm`)}
    >
      {script}
    </Script>
  );
};

export { AffirmProvider, useAffirm };
